import { db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { type Account } from "@aircap/common/account";
import {
  accountMemberInvitationsFullPath,
  type AccountMemberInvitation,
} from "@aircap/common/accountMemberInvitation";
import { supervisorRoles } from "@aircap/common/auth";
import { captureException } from "@sentry/vue";
import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { useRouter, type RouteLocationRaw } from "vue-router";

const treezorUserHome = async () => {
  const user = useUserStore();
  if (!user.uid) throw new Error("User ID is required");
  const snapshot = await getDocs(
    query(
      collection<Account>(db, "accounts"),
      where<Account>("userIds", "array-contains", user.uid),
      orderBy<Account>("createdAt", "desc"),
      limit(1),
    ),
  );
  const latest = snapshot.docs[0];
  if (latest) return { name: "Account", params: { accountId: latest.id } };

  const memberInvitations = await getDocs(
    query(
      collection<AccountMemberInvitation>(db, accountMemberInvitationsFullPath),
      where<AccountMemberInvitation>("uid", "==", user.uid),
      orderBy<AccountMemberInvitation>("createdAt", "desc"),
      limit(1),
    ),
  );
  const memberInvitation = memberInvitations.docs[0];
  if (memberInvitation)
    return { name: "MemberOnboarding", params: { memberInvitationId: memberInvitation.id } };
  return { name: "OpenAccount" };
};

export const useRedirect = () => {
  const user = useUserStore();
  const router = useRouter();
  const { showErrorToast } = useToast();

  const redirectToHome = async ({ replace = false }: { replace?: boolean } = {}) => {
    const navigate = (to: RouteLocationRaw) => (replace ? router.replace(to) : router.push(to));
    try {
      if (user.isKind("admin")) {
        user.setAdminSupervisorOrgId(null);
        await navigate({ name: "AdminSupervisorOrgs" });
        return;
      }
      if (user.hasRole(supervisorRoles, "supervisorOrg")) {
        await navigate({ name: "SupervisorOrgDashboard" });
        return;
      }
      if (user.isKind("user")) {
        const home = await treezorUserHome();
        await navigate(home);
        return;
      }
      await navigate({ name: "Accounts" });
    } catch (cause) {
      const error = new Error("Error redirecting to home", { cause });
      console.error(error);
      captureException(error);
      showErrorToast();
    }
  };

  return { redirectToHome };
};
